import { motion } from "framer-motion";
import { useState } from "react";
import { Switch } from "../components/ui/switch";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import { GrDocumentPdf } from "react-icons/gr";
import { Link } from "react-router-dom";

export default function PCA() {
  const [dark, setDark] = useState(false);

  return (
    <div className={dark ? "dark bg-gray-900 text-gray-100 min-h-screen" : "bg-gray-50 text-gray-900 min-h-screen"}>

      {/* HEADER */}
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700"
      >
        <h1 className="text-2xl font-bold">
          <Link to="/" className="hover:opacity-80 transition">
            Jeff Xie • Backend & Cloud Software Engineer
          </Link>
        </h1>

        <div className="flex justify-center gap-4">
          <a
            href="https://github.com/lumosityfan"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="GitHub"
          >
            <FaGithub className="text-2xl hover:scale-110 transition" />
          </a>

          <a
            href="https://www.linkedin.com/in/lumosityfan"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <FaLinkedin className="text-2xl hover:scale-110 transition" />
          </a>

          <a
            href="/JeffXie-Resume-February2026.pdf"
            aria-label="Download Resume"
            download
          >
            <GrDocumentPdf className="text-2xl hover:scale-110 transition" />
          </a>
        </div>

        <div className="flex items-center gap-2">
          <span>Dark Mode</span>
          <Switch checked={dark} onCheckedChange={() => setDark(!dark)} />
        </div>
      </motion.div>

      {/* CONTENT */}
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-10">

        {/* Title & Meta */}
        <div>
          <h2 className="text-3xl font-bold mb-2">
            PCA Implementation
          </h2>
          <p className="text-gray-700 dark:text-gray-500">
            Personal Project — Dimensionality Reduction & Face Detection
          </p>

          <div className="flex flex-wrap gap-2 mt-4">
            {["Python", "NumPy", "Linear Algebra", "Computer Vision", "Machine Learning"].map((tech) => (
              <span
                key={tech}
                className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-3 py-1 rounded-lg text-sm font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Overview */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-500 mb-2">Overview</h3>
          <p className="text-gray-700 dark:text-gray-500">
            This project implements Principal Component Analysis (PCA) from scratch to perform
            dimensionality reduction on high-dimensional datasets. The implementation is applied
            to face detection, demonstrating how PCA can extract meaningful structure from
            complex image data and dramatically reduce computational cost while preserving
            discriminative features.
          </p>
        </section>

        {/* The Problem */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-500 mb-2">The Problem</h3>
          <p className="text-gray-700 dark:text-gray-500">
            High-dimensional datasets — such as raw pixel representations of facial images —
            suffer from the curse of dimensionality: high memory costs, slow computation, and
            redundant features that hinder pattern recognition. Effective dimensionality reduction
            is essential to make such datasets tractable for downstream machine learning tasks.
          </p>
        </section>

        {/* Solution */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-500 mb-2">Solution & Contributions</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-500">
            <li>
              Implemented PCA from first principles using <strong>NumPy</strong>, including
              covariance matrix computation, eigendecomposition, and projection onto principal components.
            </li>
            <li>
              Applied the algorithm to facial image datasets to extract "eigenfaces" — the
              directions of maximum variance in pixel space.
            </li>
            <li>
              Validated reconstruction quality at varying levels of component retention,
              demonstrating the tradeoff between compression and fidelity.
            </li>
            <li>
              Used the reduced-dimension representations to support face detection and
              classification tasks.
            </li>
          </ul>
        </section>

        {/* Technical Architecture */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-500 mb-2">Technical Approach</h3>
          <p className="text-gray-700 dark:text-gray-500">
            The pipeline centers on computing the data covariance matrix and applying
            eigendecomposition to identify the axes of greatest variance. Images are projected
            onto the top-k eigenvectors, producing compact representations that retain the most
            discriminative information. This eigenface basis is then used to compare and identify
            faces in reduced-dimensional space.
          </p>
        </section>

        {/* Impact */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-500 mb-2">Impact</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-500">
            <li>Demonstrated strong grasp of linear algebra fundamentals applied to real data.</li>
            <li>Achieved significant dimensionality reduction with minimal loss of visual fidelity.</li>
            <li>Produced a reusable PCA module applicable to any high-dimensional dataset.</li>
          </ul>
        </section>

        {/* GitHub CTA */}
        <section>
          <a
            href="https://github.com/lumosityfan/pca_implementation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-lg transition"
          >
            <FaGithub className="text-lg" />
            View on GitHub
          </a>
        </section>

      </div>

      {/* FOOTER */}
      <footer className="text-center py-6 text-sm text-gray-400 dark:text-gray-600">
        &copy; 2026 Jeff Xie. All rights reserved.
      </footer>

    </div>
  );
}